import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { take } from "rxjs/operators";
import { environment } from "../../../environments/environment";
import { HomeApiService } from "./home-api.service";
import { AuthService } from "../auth/auth.service";
import { LanguageService } from "../language/language.service";
import { CorrelationIdService } from "../correlation-id.service";
import { HomeIaRateLimitService } from "../home-ia-rate-limit.service";

export type SseState = "connecting" | "connected" | "disconnected" | "error";

type LegacyHomeStreamEventType =
  | "smartQueue.updated"
  | "pulse.stateChanged"
  | "dates.detected"
  | "nextAction.changed";

export type HomeStreamEventType =
  | "decision.listChanged"
  | "decision.changed"
  | "decision.nextChanged"
  | "decision.activityAdded"
  | "decision.metricsChanged"
  | "pulse.stateChanged"
  | "dates.detected";

type RawHomeStreamEventType = HomeStreamEventType | LegacyHomeStreamEventType;

export interface HomeStreamEvent<T = unknown> {
  type: HomeStreamEventType;
  payload: T | null;
  lastEventId?: string;
  parseError?: string;
}

@Injectable({ providedIn: "root" })
export class HomeStreamService {
  private readonly baseUrl = environment.homeIaApiUrl || environment.HOME_API_BASE_URL;
  private readonly STREAM_URL = `${this.baseUrl}/stream`;

  private abortController?: AbortController;
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private reconnectDelayMs = 1000;
  private lastEventId?: string;
  private hasResyncedSinceDisconnect = false;

  private readonly eventsSubject = new Subject<HomeStreamEvent>();
  readonly events$ = this.eventsSubject.asObservable();

  private readonly connectionStateSubject = new BehaviorSubject<SseState>("disconnected");
  readonly connectionState$ = this.connectionStateSubject.asObservable();

  constructor(
    private readonly homeApiService: HomeApiService,
    private readonly authService: AuthService,
    private readonly languageService: LanguageService,
    private readonly correlationIdService: CorrelationIdService,
    private readonly rateLimitService: HomeIaRateLimitService
  ) {}

  connect(): Observable<HomeStreamEvent> {
    if (!this.abortController) {
      this.startConnection();
    }
    return this.events$;
  }

  disconnect(): void {
    this.teardownEventSource();
    this.connectionStateSubject.next("disconnected");
  }

  private startConnection(): void {
    this.connectionStateSubject.next("connecting");
    this.authService
      .getAccessToken()
      .pipe(take(1))
      .subscribe({
        next: (token) => {
          if (!token) {
            this.connectionStateSubject.next("error");
            this.triggerResyncOnce();
            this.scheduleReconnect();
            return;
          }

          const headers: Record<string, string> = {
            Authorization: `Bearer ${token}`,
            "Accept-Language": this.getHomeIaLanguage()
          };

          const correlationId = this.correlationIdService.get();
          if (correlationId) {
            headers["X-Correlation-Id"] = correlationId;
          }

          if (this.lastEventId) {
            headers["Last-Event-ID"] = this.lastEventId;
          }

          this.abortController = new AbortController();
          this.startFetchStream(headers, this.abortController.signal);
        },
        error: () => {
          this.connectionStateSubject.next("error");
          this.triggerResyncOnce();
          this.scheduleReconnect();
        }
      });
  }

  private dispatchEvent(type: RawHomeStreamEventType, data: string, lastEventId?: string): void {
    if (lastEventId) {
      this.lastEventId = lastEventId;
    }
    const { payload, parseError } = this.parsePayload(data);
    this.eventsSubject.next({ type: this.normalizeEventType(type), payload, lastEventId, parseError });
  }

  private normalizeEventType(type: RawHomeStreamEventType): HomeStreamEventType {
    switch (type) {
      case "smartQueue.updated":
        return "decision.listChanged";
      case "nextAction.changed":
        return "decision.nextChanged";
      default:
        return type;
    }
  }

  private parsePayload(raw: unknown): { payload: unknown; parseError?: string } {
    if (raw === null || raw === undefined || raw === "") {
      return { payload: null };
    }

    if (typeof raw !== "string") {
      return { payload: raw };
    }

    try {
      return { payload: JSON.parse(raw) };
    } catch (error) {
      return { payload: null, parseError: (error as Error).message };
    }
  }

  private async startFetchStream(headers: Record<string, string>, signal: AbortSignal): Promise<void> {
    try {
      const response = await fetch(this.STREAM_URL, {
        method: "GET",
        headers,
        signal
      });

      if (!response.ok || !response.body) {
        this.connectionStateSubject.next("error");
        this.triggerResyncOnce();
        this.scheduleReconnect();
        return;
      }

      const correlationId = response.headers.get("X-Correlation-Id") || undefined;
      if (correlationId) {
        this.correlationIdService.set(correlationId);
      }
      const retryAfter = response.headers.get("Retry-After");
      const retrySeconds = retryAfter ? Number(retryAfter) : undefined;
      if (retrySeconds && !Number.isNaN(retrySeconds)) {
        this.rateLimitService.setRetryAfterSeconds(retrySeconds);
      }

      this.reconnectDelayMs = 1000;
      this.hasResyncedSinceDisconnect = false;
      this.connectionStateSubject.next("connected");

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) {
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        let splitIndex: number;
        while ((splitIndex = buffer.indexOf("\n\n")) !== -1) {
          const rawEvent = buffer.slice(0, splitIndex);
          buffer = buffer.slice(splitIndex + 2);
          this.handleRawEvent(rawEvent);
        }
      }

      this.connectionStateSubject.next("error");
      this.triggerResyncOnce();
      this.scheduleReconnect();
    } catch {
      if (!signal.aborted) {
        this.connectionStateSubject.next("error");
        this.triggerResyncOnce();
        this.scheduleReconnect();
      }
    }
  }

  private handleRawEvent(rawEvent: string): void {
    if (!rawEvent.trim()) {
      return;
    }

    let eventType: RawHomeStreamEventType | undefined;
    let data = "";
    let lastEventId: string | undefined;

    rawEvent.split("\n").forEach((line) => {
      if (line.startsWith("event:")) {
        eventType = line.slice("event:".length).trim() as RawHomeStreamEventType;
      } else if (line.startsWith("data:")) {
        const chunk = line.slice("data:".length).trim();
        data = data ? `${data}\n${chunk}` : chunk;
      } else if (line.startsWith("id:")) {
        lastEventId = line.slice("id:".length).trim();
      }
    });

    if (eventType) {
      this.dispatchEvent(eventType, data, lastEventId);
    }
  }

  private getHomeIaLanguage(): string {
    const current = this.languageService.getCurrentLang();
    return current === "en" ? "en" : "es";
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer) {
      return;
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      this.teardownEventSource();
      this.startConnection();
      this.reconnectDelayMs = Math.min(this.reconnectDelayMs * 2, 60000);
    }, this.reconnectDelayMs);
  }

  private triggerResyncOnce(): void {
    if (this.hasResyncedSinceDisconnect) {
      return;
    }
    this.hasResyncedSinceDisconnect = true;

    this.homeApiService.getDecisions().subscribe({ error: () => undefined });
    this.homeApiService.getPulse().subscribe({ error: () => undefined });
    this.homeApiService.getNextDecision().subscribe({ error: () => undefined });
    this.homeApiService.getDatesSummary().subscribe({ error: () => undefined });
    this.homeApiService.getMetrics().subscribe({ error: () => undefined });
  }

  private teardownEventSource(): void {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }
  }
}